// Iter 82.7 / Phase E2.3 — Daily Attendance Summary Report.
// Per-(employee, date) row with KPI strip, filters, CSV + XLSX exports.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, FileXls, FunnelSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { formatDate, formatTime } from "../../lib/format";

const STATUS_TONE = {
    Present: "bg-emerald-100 text-emerald-700",
    "Half Day": "bg-amber-100 text-amber-700",
    Absent: "bg-rose-100 text-rose-700",
    "On Leave": "bg-sky-100 text-sky-700",
    Holiday: "bg-indigo-100 text-indigo-700",
    "Weekly Off": "bg-slate-100 text-slate-600",
};

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function AttendanceDailySummaryReport() {
    const [filters, setFilters] = useState({
        from_date: todayIso(),
        to_date: todayIso(),
        department: "",
        attendance_status: "",
        employee_id: "",
    });
    const [data, setData] = useState({ items: [], summary: {} });
    const [loading, setLoading] = useState(true);
    const [hrMeta, setHrMeta] = useState({ departments: [] });
    const [attMeta, setAttMeta] = useState({ attendance_statuses: [] });

    const loadMeta = useCallback(async () => {
        try {
            const [hr, att] = await Promise.all([
                api.get("/hr/employee/meta"),
                api.get("/attendance/masters/meta"),
            ]);
            setHrMeta(hr.data);
            setAttMeta(att.data);
        } catch (e) { /* meta is non-blocking */ }
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: payload } = await api.get("/attendance/report/daily-summary", { params });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load daily summary"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);
    useEffect(() => { reload(); }, [reload]);

    const download = async (ext) => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: blob } = await api.get(`/attendance/report/daily-summary/export.${ext}`, { params, responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `attendance-daily-${filters.from_date}_${filters.to_date}.${ext}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, `${ext.toUpperCase()} export failed`));
        }
    };

    const s = data.summary || {};

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="att-daily-page">
            <PageHeader
                eyebrow="Attendance Reports"
                title="Daily Attendance Summary"
                description={`Per-employee row for the selected date range · ${data.total || 0} record${data.total === 1 ? "" : "s"}`}
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="att-daily-csv-btn">
                            <DownloadSimple size={14} className="mr-1" /> CSV
                        </Button>
                        <Button size="sm" onClick={() => download("xlsx")} data-testid="att-daily-xlsx-btn">
                            <FileXls size={14} className="mr-1" /> XLSX
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="att-daily-filters">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">From Date</Label>
                        <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="att-daily-from" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">To Date</Label>
                        <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="att-daily-to" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Department</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={filters.department}
                                onChange={(e) => setFilters({ ...filters, department: e.target.value })} data-testid="att-daily-dept">
                            <option value="">All</option>
                            {(hrMeta.departments || []).map((d, i) => {
                                const name = typeof d === "string" ? d : (d.name || "");
                                const key = typeof d === "string" ? d : (d.id || d.code || d.name || `dep-${i}`);
                                return <option key={key} value={name}>{name}</option>;
                            })}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Status</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={filters.attendance_status}
                                onChange={(e) => setFilters({ ...filters, attendance_status: e.target.value })} data-testid="att-daily-status">
                            <option value="">All</option>
                            {(attMeta.attendance_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <Button variant="outline" size="sm" className="w-full"
                                onClick={() => setFilters({ from_date: todayIso(), to_date: todayIso(), department: "", attendance_status: "", employee_id: "" })}
                                data-testid="att-daily-clear">
                            <FunnelSimple size={14} className="mr-1" /> Clear
                        </Button>
                    </div>
                </div>
            </section>

            <section className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="att-daily-kpis">
                <Kpi label="Total Rows" value={s.total_rows || 0} testid="kpi-total" tone="slate" />
                <Kpi label="Present" value={s.present_count || 0} testid="kpi-present" tone="emerald" />
                <Kpi label="Absent" value={s.absent_count || 0} testid="kpi-absent" tone="rose" />
                <Kpi label="Half Day" value={s.half_day_count || 0} testid="kpi-halfday" tone="amber" />
                <Kpi label="On Leave" value={s.on_leave_count || 0} testid="kpi-leave" tone="sky" />
                <Kpi label="Late" value={s.late_count || 0} testid="kpi-late" tone="orange" />
                <Kpi label="Anomalies" value={s.anomaly_total || 0} testid="kpi-anomalies" tone="amber" />
                <Kpi label="Regularized" value={s.regularized_count || 0} testid="kpi-regularized" tone="violet" />
            </section>

            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="att-daily-table">
                {/* Iter 102.2 · single IST disclaimer above the dense report table. */}
                <div className="flex justify-end px-4 py-2 border-b border-slate-100 bg-slate-50/50">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400" data-testid="ist-disclaimer">All times are displayed in IST.</span>
                </div>
                {loading ? (
                    <div className="text-center py-8 text-sm text-slate-400">Loading…</div>
                ) : data.items.length === 0 ? (
                    <div className="text-center py-12 text-sm text-slate-400">No records for the selected filters.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Date</th>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Department</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Check-In</th>
                                <th className="text-left px-3 py-2 font-medium">Check-Out</th>
                                <th className="text-right px-3 py-2 font-medium">Net Hrs</th>
                                <th className="text-right px-3 py-2 font-medium">Late</th>
                                <th className="text-left px-3 py-2 font-medium">Source</th>
                                <th className="text-right px-3 py-2 font-medium" title="Active / Idle / Locked (minutes) — Desktop Agent mode only">Active / Idle / Locked</th>
                                <th className="text-right px-3 py-2 font-medium">Gaps</th>
                                <th className="text-right px-3 py-2 font-medium">Anomalies</th>
                                <th className="text-left px-3 py-2 font-medium">Drill</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((r, idx) => (
                                <tr key={`${r.employee_id}-${r.attendance_date}-${idx}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`daily-row-${idx}`}>
                                    <td className="px-3 py-2 text-xs font-mono">{r.attendance_date}</td>
                                    <td className="px-3 py-2">
                                        <div className="text-sm font-semibold">{r.employee_name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{r.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{r.department || "—"}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-xs ${STATUS_TONE[r.attendance_status] || "bg-slate-100"}`}>{r.attendance_status}</Badge>
                                        {r.is_late && <Badge className="ml-1 bg-orange-100 text-orange-700 text-xs">Late</Badge>}
                                        {r.is_regularized && <Badge className="ml-1 bg-violet-100 text-violet-700 text-xs">Regularized</Badge>}
                                        {r.needs_review && <Badge className="ml-1 bg-amber-100 text-amber-800 text-xs" title={r.needs_review_reason || ""}>Needs Review</Badge>}
                                    </td>
                                    <td className="px-3 py-2 text-xs font-mono">{formatTime(r.check_in_time)}</td>
                                    <td className="px-3 py-2 text-xs font-mono">{formatTime(r.check_out_time)}</td>
                                    <td className="px-3 py-2 text-right">{((r.net_working_minutes || 0) / 60).toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right">{r.late_minutes || 0}</td>
                                    <td className="px-3 py-2 text-xs" data-testid={`daily-source-${idx}`}>
                                        <SourceBadge source={r.attendance_source} mode={r.attendance_source_mode} />
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs font-mono" data-testid={`daily-agent-mins-${idx}`}>
                                        {(r.agent_total_active_minutes || r.agent_total_idle_minutes || r.agent_total_locked_minutes)
                                            ? (
                                                <span title={`Active ${Math.round(r.agent_total_active_minutes || 0)}m · Idle ${Math.round(r.agent_total_idle_minutes || 0)}m · Locked ${Math.round(r.agent_total_locked_minutes || 0)}m`}>
                                                    <span className="text-emerald-700">{Math.round(r.agent_total_active_minutes || 0)}</span>
                                                    <span className="text-slate-400 mx-0.5">/</span>
                                                    <span className="text-amber-700">{Math.round(r.agent_total_idle_minutes || 0)}</span>
                                                    <span className="text-slate-400 mx-0.5">/</span>
                                                    <span className="text-rose-700">{Math.round(r.agent_total_locked_minutes || 0)}</span>
                                                </span>
                                            )
                                            : <span className="text-slate-300">—</span>
                                        }
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs" data-testid={`daily-gaps-${idx}`}>
                                        {(r.agent_long_gap_count || 0) > 0
                                            ? <Badge className="bg-rose-100 text-rose-800 text-xs" title={`${r.agent_long_gap_count} long offline gap(s)`}>{r.agent_long_gap_count}</Badge>
                                            : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right">{r.anomaly_count || 0}</td>
                                    <td className="px-3 py-2">
                                        <Link to={`/admin/attendance/snapshots?employee_id=${r.employee_id}&from_date=${r.attendance_date}&to_date=${r.attendance_date}`}
                                              className="text-xs text-sky-700 hover:underline flex items-center gap-1" data-testid={`daily-drill-${idx}`}>
                                            <ArrowSquareOut size={12} /> View
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}

function Kpi({ label, value, tone = "slate", testid }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        orange: "bg-orange-50 border-orange-200 text-orange-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
    };
    return (
        <div className={`rounded-lg border p-3 ${map[tone]}`} data-testid={testid}>
            <div className="text-[9px] uppercase tracking-wider font-semibold">{label}</div>
            <div className="text-xl font-bold mt-1">{value}</div>
        </div>
    );
}

// Iter 98 / Phase E2.4 — Attendance source badge.
// Maps both event-source values AND source-mode values so the column
// reads naturally regardless of which one is set on the row.
function SourceBadge({ source, mode }) {
    if (!source && !mode) return <span className="text-slate-300">—</span>;
    const label = ({
        // event_source values (winning source on the day)
        "desktop_agent_bridge": "Desktop Agent",
        "manual_fallback": "Manual Fallback",
        "web": "Manual ESS",
        "mobile_web": "Manual ESS",
        "biometric": "Biometric",
        "admin_correction": "Admin Correction",
        "regularization": "Regularization",
        // source-mode values
        "desktop_agent": "Desktop Agent",
        "hybrid": "Hybrid",
        "manual": "Manual ESS",
    })[source || mode] || (source || mode);
    const tone = ({
        "desktop_agent_bridge": "bg-violet-100 text-violet-800",
        "manual_fallback": "bg-amber-100 text-amber-800",
        "web": "bg-sky-100 text-sky-800",
        "mobile_web": "bg-sky-100 text-sky-800",
        "biometric": "bg-emerald-100 text-emerald-800",
        "admin_correction": "bg-rose-100 text-rose-800",
        "regularization": "bg-indigo-100 text-indigo-800",
        "desktop_agent": "bg-violet-100 text-violet-800",
        "hybrid": "bg-fuchsia-100 text-fuchsia-800",
        "manual": "bg-sky-100 text-sky-800",
    })[source || mode] || "bg-slate-100 text-slate-700";
    return <Badge className={`text-[10px] ${tone}`}>{label}</Badge>;
}
